import React, { useRef, useState } from "react";
import { API, getToken } from "../util/auth";

type ParsedData = {
  patient?: {
    sex?: string;
    age?: string | number;
  };
  motor?: Array<Record<string, string>>;
  sensory?: Array<Record<string, string>>;
  fwave?: Array<Record<string, string>>;
  hreflex?: Array<Record<string, string>>;
  emg?: Array<Record<string, string>>;
};

interface FileUploaderProps {
  onParsed: (data: ParsedData) => void;
}

const FileUploader: React.FC<FileUploaderProps> = ({ onParsed }) => {
  const [file, setFile] = useState<File | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => fileInputRef.current?.click();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith(".docx")) {
      alert("Wrong file format. Please upload a .docx file.");
      event.target.value = "";
      return;
    }

    setFile(selectedFile);
  };

  const handleParseClick = async () => {
    if (!file) {
      alert("Please upload a .docx file first.");
      return;
    }

    const token = getToken();
    const authHeader = { Authorization: `Bearer ${token}` };

    const formData = new FormData();
    formData.append("file", file);

    setIsParsing(true);

    try {
      const response = await fetch(`${API}/parse-all`, {
        method: "POST",
        headers: authHeader,
        body: formData,
      });

      if (!response.ok) {
        let message = "Failed to parse the document.";
        try {
          const payload = await response.json();
          message = payload.detail || payload.message || message;
        } catch {
          // noop
        }
        throw new Error(message);
      }

      const parsed = (await response.json()) as ParsedData;
      onParsed(parsed);
    } catch (error) {
      console.error("Error parsing file:", error);
      alert(error instanceof Error ? error.message : "Failed to parse the document.");
    } finally {
      setIsParsing(false);
    }
  };

  return (
    <section className="surface-card upload-card">
      <div className="card-header">
        <h2 className="card-title">Exam Upload</h2>
        <div className="upload-actions">
          {file && <span className="file-pill">{file.name}</span>}
          <button type="button" onClick={handleButtonClick} className="app-button secondary small">
            {file ? "Change File" : "Choose DOCX"}
          </button>

          <button
            type="button"
            onClick={handleParseClick}
            className="app-button primary small"
            disabled={!file || isParsing}
          >
            {isParsing ? "Loading..." : "Load Exam Result"}
          </button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".docx"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </section>
  );
};

export default FileUploader;
